import { BaseApi, type SuccessResponse, type ItemResponse } from '@bv/api/base';

/**
 * Defines the type of user, which can either be an employee or a client.
 * @typedef {'employee' | 'client'} UserType
 */
export type UserType = 'employee' | 'client';

/**
 * @interface LoginCredentials
 * Defines the payload for a standard email and password login request.
 */
export interface LoginCredentials {
  email: string;
  password: string;
  type: UserType;
  remember_me?: boolean;
}

/**
 * @interface LoginOtpPayload
 * Defines the payload for verifying a login one-time password (OTP).
 */
export interface LoginOtpPayload {
  email: string;
  code: string;
  use_here: boolean;
  remember_me?: boolean;
}

/**
 * @interface ForgetPasswordPayload
 * Defines the payload to initiate the forgot password process.
 */
export interface ForgetPasswordPayload {
  email: string;
  type: UserType;
  token: string;
}

/**
 * @interface ForgetPasswordOtpPayload
 * Defines the payload for verifying a password reset token sent to the user's email.
 */
export interface ForgetPasswordOtpPayload {
  email: string;
  token: string;
  public_token: string;
}

/**
 * @interface ResetPasswordOtpPayload
 * Defines the payload for setting a new password after verifying a reset token.
 */
export interface ResetPasswordOtpPayload extends ForgetPasswordOtpPayload {
  password: string;
  password_confirmation: string;
}

/**
 * @interface Token
 * Represents the structure of authentication tokens returned by the API.
 */
export interface Token {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  expires_at?: number;
  refresh_expires_in: number;
  token_type: 'Bearer';
}

/**
 * @interface LoginOtpObject
 * Represents the user data and tokens returned after a successful OTP verification.
 */
export interface LoginOtpObject {
  id: string;
  username: string;
  email: string;
  img: null | string;
  type: UserType;
  token: Token;
  recent_workspaces: null | string;
  recent_service: null | string;
  workspaces: [];
}

/**
 * @interface forgetPasswordObject
 * Represents token that stored to use in resetPassword requests
 */
export interface forgetPasswordObject {
  expires_at: string;
  token: string;
}

/**
 * Manages all authentication-related API requests.
 */
export class AuthApi extends BaseApi {
  /**
   * Initializes a new instance of the AuthApi.
   * Sets the base endpoint to 'auth'.
   */
  public constructor() {
    super('auth');
  }

  /**
   * Sends a login request with user credentials.
   * @param {LoginCredentials} credentials - The user's email, password, and type.
   * @returns {Promise<SuccessResponse>} A promise that resolves upon a successful request.
   */
  public async login(credentials: LoginCredentials): Promise<SuccessResponse> {
    return this.request<LoginCredentials, SuccessResponse>({
      method: 'post',
      url: 'login',
      withCredentials: true,
      data: credentials,
      headers: { 'x-skip-refresh': 'true', 'x-silent': 'true', 'x-return-full-error': 'true' },
    });
  }

  /**
   * Verifies a One-Time Password (OTP) to complete a login process.
   *
   * @param {LoginOtpPayload} data - The user's email, the OTP code, and session options.
   * @returns {Promise<LoginOtpObject>} A promise that resolves with the authenticated
   * user's data and tokens.
   */
  public async verifyOtp(data: LoginOtpPayload): Promise<LoginOtpObject> {
    return this.request<LoginOtpPayload, ItemResponse<LoginOtpObject>>({
      method: 'post',
      url: 'verify-login-otp',
      data,
    }).then((response) => response.data);
  }

  /**
   * Initiates an impersonation session for a specific user within a workspace.
   * This is typically an administrator-only action. It retrieves an impersonation
   * token from the server.
   *
   * @param {object} data - The identifiers for the impersonation session.
   * @param {string} data.workspaceId - The ID of the workspace to impersonate the user in.
   * @param {string} data.userId - The ID of the user to impersonate.
   * @returns {Promise<ItemResponse<LoginOtpObject>>} A promise that resolves with the
   * response containing the impersonation token and OTP details.
   */
  public async loginAsImpersonation({
    workspaceId,
    userId,
  }: {
    workspaceId: string;
    userId: string;
  }): Promise<ItemResponse<LoginOtpObject>> {
    return this.request<void, ItemResponse<LoginOtpObject>>({
      method: 'post',
      absoluteURL: `iam/admins/users/${userId}/workspaces/${workspaceId}/impersonate`,
    });
  }

  /**
   * Initiates the forgot password process for a user.
   * @param {ForgetPasswordPayload} data - The user's email and type.
   * @returns {Promise<ItemResponse<forgetPasswordObject>>} A promise that resolves with the
   * response containing the token to store to use in resetPassword request
   */
  public async forgotPassword(data: ForgetPasswordPayload): Promise<ItemResponse<forgetPasswordObject>> {
    return this.request<ForgetPasswordPayload, ItemResponse<forgetPasswordObject>>({
      method: 'post',
      url: 'forgot-password',
      data,
    });
  }

  /**
   * Verifies the password reset token sent to the user.
   * @param {ForgetPasswordOtpPayload} data - The user's email and the reset token.
   * @returns {Promise<SuccessResponse>} A promise that resolves upon successful verification.
   */
  public async forgotPasswordOtp(data: ForgetPasswordOtpPayload): Promise<SuccessResponse> {
    return this.request<ForgetPasswordOtpPayload, SuccessResponse>({
      method: 'post',
      url: 'verify-password-token',
      data,
    });
  }

  /**
   * Resets the user's password using a verified token.
   * @param {ResetPasswordOtpPayload} payload - The email, token, and new password details.
   * @returns {Promise<SuccessResponse>} A promise that resolves upon successful password reset.
   */
  public async resetPassword(data: ResetPasswordOtpPayload): Promise<SuccessResponse> {
    return this.request<ResetPasswordOtpPayload, SuccessResponse>({ method: 'post', url: 'reset-password', data });
  }

  /**
   * Sets a user's password, typically for an invitation flow.
   * @param {ResetPasswordOtpPayload} data - The invitation token and new password details.
   * @returns {Promise<SuccessResponse>} A promise that resolves upon successfully setting the password.
   */
  public async invitation(data: ResetPasswordOtpPayload): Promise<SuccessResponse> {
    return this.request<ResetPasswordOtpPayload, SuccessResponse>({ method: 'post', url: 'password/set', data });
  }

  /**
   * Sends a logout request to invalidate the current session on the server.
   *
   * @returns {Promise<SuccessResponse>} A promise that resolves with a success message
   * from the server after the session is invalidated.
   */
  public async logout(): Promise<SuccessResponse> {
    return this.request<ResetPasswordOtpPayload, SuccessResponse>({
      method: 'post',
      url: 'logout',
      absoluteURL: 'iam/users',
      headers: { 'x-logout': 'true' },
    });
  }
}

/**
 * A singleton instance of the AuthApi class for convenient access throughout the application.
 */
export const authApi: AuthApi = new AuthApi();
